/**
 * Centralized date formatting utilities for French format (DD/MM/YYYY)
 */

/**
 * Format a date to French format (DD/MM/YYYY)
 * @param date - Date to format
 * @returns Formatted date string (DD/MM/YYYY)
 */
export const formatDateFrench = (date: Date | undefined): string => {
  if (!date) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Format a date for use in date input fields (YYYY-MM-DD)
 * @param date - Date to format
 * @returns Formatted date string (YYYY-MM-DD)
 */
export const formatDateForInput = (date: Date | undefined): string => {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Parse a French format date string (DD/MM/YYYY) to ISO format (YYYY-MM-DD)
 * @param frenchDate - Date string in DD/MM/YYYY format
 * @returns ISO format date string (YYYY-MM-DD)
 */
export const parseDateFromFrench = (frenchDate: string): string => {
  const parts = frenchDate.split("/");
  if (parts.length !== 3) return "";
  const [day, month, year] = parts;
  return `${year}-${month}-${day}`;
};

/**
 * Parse an ISO format date string (YYYY-MM-DD) to French format (DD/MM/YYYY)
 * @param isoDate - Date string in YYYY-MM-DD format
 * @returns French format date string (DD/MM/YYYY)
 */
export const parseDateToFrench = (isoDate: string): string => {
  const parts = isoDate.split("-");
  if (parts.length !== 3) return "";
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
};

/**
 * Parse a date string from date input field (YYYY-MM-DD) to a Date object
 * Creates date in local timezone to avoid off-by-one errors
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object in local timezone
 */
export const parseDateFromInput = (dateString: string): Date => {
  if (!dateString) return new Date();

  // Parse the string manually to avoid timezone issues
  const [year, month, day] = dateString.split("-").map(Number);

  // Month is 0-based in Date constructor, but 1-based in the input string
  return new Date(year, month - 1, day);
};
